import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';

/**
 * Get the first character off a string
 */
export const getFirstChar = (str: string) => (str.trim().length ? str.trim()[0].toUpperCase() : '');

/**
 * Given a list of strings, return the first char from each as uppercase
 */
export const getInitials = (strings: string[]) =>
  strings
    .map((string = '') => string.trim().slice(0, 1))
    .join('')
    .toUpperCase();

/**
 * Given a string of first and last name, return an object containing them
 */
export const getFistAndLastNameFromFullName = (fullName: string) => {
  const nameArray = fullName?.split(' ') || [];
  return {
    firstName: nameArray?.[0] || '',
    lastName: nameArray?.[nameArray?.length - 1] || '',
  };
};

/**
 * Given a url, return the file name (if one is referenced).
 * Returns an empty string if there is any problem with parsing the URL string.
 */
export const getFileNameFromUrl = (url: string) =>
  pipe(
    E.tryCatch(
      () => new URL(url), // try
      (e) => (e instanceof Error ? e : new Error('unknown error')), // handle catch
    ),
    E.map((u) => u.pathname),
    E.map((p) => p.split('/').pop() ?? ''),
    E.filterOrElse(
      isValidFilename, // filter
      () => new Error('invalid filename'), // convert false to error
    ),
    E.getOrElse(() => ''), // Ignore errors
  );

/**
 If an index is found at the start of a string, remove it and return the rest of the string
 */
export const stripPrefix = (prefix: string, stringVal: string) => {
  if (stringVal.indexOf(prefix) === 0) {
    return stringVal.slice(prefix.length);
  }

  return stringVal;
};

/** 
 Capitalizes the first letter of a string and lowercases everything else 
 */
export const sentenceCase = (str: string) => {
  return `${str[0].toUpperCase()}${str.substring(1).toLowerCase()}`;
};

/**
 * Given a string, format it to kebabCase
 */
export const kebabCase = (str: string) => {
  return `${str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.join('-')
    .toLowerCase()}`;
};
/**
 * Given a string, format to camelCase ie: "Dogs Are Cute" --> "dogsAreCute"
 * @param str the string to test
 */
export const camelCase = (str: string) => {
  const newString = str.trim().split(' ').map(sentenceCase).join('');
  return `${newString[0].toLowerCase()}${newString.substring(1)}`;
};
/**
 * Determines if the last character in a string is letter
 * @param str - the string to test
 */
export const isCharAlpha = (str: string) => /\p{Letter}$/u.test(str);

interface TrimConfig {
  /** The horizontal space for text within the component. Likely computed with something like https://github.com/streamich/react-use/blob/master/docs/useSize.md */
  containerWidth: number;
  /** The font size of the text in question. Used to determine approximate width of each character */
  fontSize: number;
  /** The number of lines to limit the text to */
  numLines: number;
  /** A given ratio to approximate the width of each character */
  ratio: number;
}

/**
 * A function that takes a string and trims it down to a set number of lines with a contextual `...` overflow.
 * DISCLAIMER: because ratio is an approximation, going for a "best fit" might sometimes spill onto an extra line.
 *
 * CSS ellipsis overflows only work on single lines, so this solution:
 * 1. Takes the width of a container
 * 2. Takes some supplemental information about the font used and number of lines desired
 * 3. Calculates an approximate value for the width of each character in the string
 * 4. Trims down the string to the number of characters that can fit in numLines * width
 * 5. Returns the string, appends a `...` if it ends on a letter
 */
export const fitTextToNumLines = (
  text = '',
  { containerWidth, fontSize, numLines, ratio }: TrimConfig,
): string => {
  // the container width is the allowed length of one line, each allowed line ups the allowed "length" of the string
  const allowedLength = numLines * containerWidth;
  // we give a rough ratio to calculate the width of each character in pixels
  const charWidth = fontSize * ratio;
  // dividing the allowedLength by the rough character width gives us # of allowed chars
  const numCharsAllowed = allowedLength / charWidth;

  // slice off the front of the string matching the num of allowed chars
  // don't end on a space!
  const truncatedText = text.split('').slice(0, numCharsAllowed).join('').trim();

  // eject on zero length strings
  if (!truncatedText.length) {
    return text;
  }

  // if the last character in the string is an alpha char we add the ellipsis
  const lastCharIsAlpha = isCharAlpha(truncatedText[truncatedText.length - 1]);

  if (lastCharIsAlpha) {
    return `${truncatedText}...`;
  }

  return truncatedText;
};

export const isEmptyString = (value?: string, trim = true): value is undefined => {
  if (!value) {
    return true;
  }

  return trim ? value.trim() === '' : value === '';
};

export const isValidJson = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const isValidJsObj = (obj: object) => {
  try {
    JSON.stringify(obj);
  } catch (e) {
    return false;
  }
  return true;
};

export const safelyStringifyObject = (object: any) => {
  try {
    return JSON.stringify(object);
  } catch (e) {
    // oh well, we tried.
  }
};

// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s01.html
// TLD length is valid up to 63 characters. Though uncommon, we should allow for this. #DLVR-1325
// https://developer.mozilla.org/en-US/docs/Learn/Common_questions/What_is_a_domain_name
export const isValidEmail = (str: string) => {
  return /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[A-Z0-9-]+\.)+[A-Z]{2,63}$/i.test(
    str,
  );
};

// https://ihateregex.io/expr/uuid/#
export const isValidUuid = (str: string) => {
  return /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gm.test(
    str,
  );
};

export const isValidFilename = (str: string) => /^[\w\s\-]+\.[a-z0-9]{2,4}$/i.test(str);

export const isTextOnly = (str: string) => {
  const div = document.createElement('div');
  div.innerHTML = str;
  return div.textContent === str; // alternatively .innerText
};

export const getTextOnly = (str: string) => {
  const div = document.createElement('div');
  div.innerHTML = str;
  return div.textContent;
};

export const excludeEmptyStrings = (strArray: string[]) => strArray.filter((str) => str.length);

export const parenthesize = (content: string) => `(${content})`;

export const joinWithSpaces = (strArray: string[]) => excludeEmptyStrings(strArray).join(' ');

export const joinWithDots = (strArray: string[]) => excludeEmptyStrings(strArray).join('.');

export const joinWithEquals = (strArray: string[]) => excludeEmptyStrings(strArray).join('=');

export const joinWithNewLine = (strArray: string[]) => excludeEmptyStrings(strArray).join('\n');

export const concatenate = (strArray: string[]) => strArray.join('');
